import React, { createContext, useContext, useEffect, useState } from 'react'

import { IS_PRODUCTION, KEYS } from '@lyrahealth-inc/shared-app-logic'

interface DevToolsContextType {
  isDevToolsOpen: boolean
  openDevTools: () => void
  closeDevTools: () => void
}

const DevToolsContext = createContext<DevToolsContextType | undefined>(undefined)

export const useDevTools = () => {
  const context = useContext(DevToolsContext)
  if (!context) {
    throw new Error('useDevTools must be used within DevToolsProvider')
  }
  return context
}

interface DevToolsProviderProps {
  children: React.ReactNode
}

export const DevToolsProvider: React.FC<DevToolsProviderProps> = ({ children }) => {
  const [isDevToolsOpen, setIsDevToolsOpen] = useState(false)

  const openDevTools = () => setIsDevToolsOpen(true)
  const closeDevTools = () => setIsDevToolsOpen(false)

  useEffect(() => {
    if (IS_PRODUCTION) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + Shift + 1 to toggle dev tools
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === KEYS.ONE) {
        event.preventDefault()
        setIsDevToolsOpen((prev) => !prev)
      }
      // Escape to close
      if (event.key === KEYS.ESC && isDevToolsOpen) {
        closeDevTools()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isDevToolsOpen])

  return (
    <DevToolsContext.Provider value={{ isDevToolsOpen, openDevTools, closeDevTools }}>
      {children}
    </DevToolsContext.Provider>
  )
}
